// src/components/PatientDetails/PatientDetailsLayout.tsx
// -----------------------------------------------------------------------------
// PatientDetailsLayout - Responsive sidebar layout for patient details
// -----------------------------------------------------------------------------
// Desktop (>1200px): Sidebar visible | Data content | Floating chat
// Tablet/Mobile (<1200px): Sidebar as drawer | Data content | Floating chat
// Classic 3-column layout available via localStorage toggle
// -----------------------------------------------------------------------------

import { Box, useMediaQuery, useTheme, Fab, Drawer, alpha, Tooltip, Typography, IconButton, Popover, List, ListItemButton, ListItemText, Divider } from "@mui/material";
import { 
  ChevronLeft,
  ChevronRight,
  ViewColumn,
  ViewSidebar,
} from "@mui/icons-material";
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import ResizablePanel from "../ui/ResizablePanel";
import { DataPanel, ChatPanel, ActionPanel, FloatingChatPanel, TableSidebar, anonymizeName } from "./panels";
import { SIDEBAR_WIDTH } from "./panels/TableSidebar";
import { EvidenceDocument } from "types";
import { useTranslation } from "../../i18n";
import { createData } from "api/apiHelpers";
import { useFieldSchema } from "hooks/useFieldSchema";
import logger from "utils/logger";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type DomainType = { value: string; label: string };

/** Row of extracted clinical data — keyed by field name, value varies by domain schema */
type TableRow = Record<string, string | number | boolean | null | undefined>;

type DomainTablesData = {
  event: TableRow[];
  static: TableRow[];
};

type UniversalTablesData = {
  patientProfile: TableRow[];
  molecularProfile: TableRow[];
  cancerProfile: TableRow[];
  cancerObservation: TableRow[];
  hematologicalProfile: TableRow[];
  trialEnrollment: TableRow[];
  adverseEvents: TableRow[];
  medications: TableRow[];
  diagnoses: TableRow[];
  procedures: TableRow[];
  tumorAssessments: TableRow[];
  diseaseStatuses: TableRow[];
  imaging: TableRow[];
  systemicTherapies: TableRow[];
  labResults: TableRow[];
  familyHistory: TableRow[];
};

interface PatientDetailsLayoutProps {
  /** Patient ID */
  patientId?: string | number;
  /** Patient name (will be anonymized for display) */
  patientName?: string;
  /** Pre-anonymized name from backend (preferred if available) */
  anonymizedPatientName?: string;
  /** Environment names the patient belongs to */
  environmentNames?: string[];
  /** Patient.modified_at (DynamicModel, auto_now). Row write time, not a
   *  clinical event time — the header labels it "Updated" for that reason. */
  patientUpdatedAt?: string;
  /** Patient.domains (JSONField on the model), e.g. ["cancer.skin"]. The
   *  authoritative list — the header card's own tablesData.domains is empty
   *  there, which is also why its body map draws a plain silhouette. */
  patientDomains?: string[];
  /** Whether this caller may reveal the raw name/external_id (from PatientSerializer). */
  canRevealPii?: boolean;
  /** Institution external ID */
  externalId?: string;
  /** Legacy cohort ID */
  legacyId?: string;
  /** Available domain types */
  domainTypes: DomainType[];
  /** Currently selected domains */
  selectedDomains: string[];
  /** Handler for domain selection change */
  onDomainSelectionChange: (domains: string[]) => Promise<{ limited: boolean; max: number }>;
  /** Suggested domains from AI */
  suggestedDomains?: string[];
  /** Tables data from backend */
  tablesData: {
    domains: { [key: string]: DomainTablesData };
    universal: UniversalTablesData;
  };
  /** Tables loading state (data fetch only, not AI extraction) */
  tablesLoading?: boolean;
  /** True only when the AI extraction pipeline is actively running */
  isAIExtracting?: boolean;
  /** Callback when tables data changes */
  onTablesDataChange?: (domain: string, type: "event" | "static", data: TableRow[]) => void;
  /** Callback to add a domain to the active selection (auto-activate from (+) button) */
  onDomainAdd?: (domainKey: string) => void;
  /** Callback when a new clinical data record is created (triggers refresh) */
  onRecordCreated?: () => void;
  /** Submitted documents (visualizable files only) */
  submittedDocuments: EvidenceDocument[];
  /** All documents including text-only (compatible with Document type from useDocuments) */
  allDocuments?: Array<{
    id: string;
    file: string;
    file_name: string;
    file_url?: string;
    text?: string;
    created_at?: string;
  }>;
  /** Handler for document delete */
  onDocumentDelete?: (id: string) => void;
  /** Handler when submission completes */
  onSubmit: () => void;
  /** Whether extraction is in progress */
  isExtracting?: boolean;
  /** Whether extraction appears stale/stuck (polling timed out) */
  isStaleProcessing?: boolean;
  /** Whether any tables have processing_status="error" */
  hasExtractionErrors?: boolean;
  /** Retirement flag (066 D / 4.0): when false, hide legacy Baseline/History sections. */
  legacyTablesEnabled?: boolean;
  /** Handler to unstick + retry extraction for this patient */
  onRetryExtraction?: () => Promise<boolean>;
  /** Handler for delete patient */
  onDeletePatient?: () => Promise<void>;
  /** Handler for share patient */
  onSharePatient?: () => void;
  /** Handler for generate report */
  onGenerateReport?: () => Promise<void>;
  /** Handler for export data */
  onExportData?: () => Promise<void>;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const SYSTEM_KEYS = new Set([
  "id", "event_id", "created_at", "modified_at", "created_by",
  "batch_id", "event_no", "text", "table", "processing_task_id",
  "processing_status", "patient", "environments", "platform_user",
  "is_validated", "progress", "eventtable_ptr", "found_with_parent",
  "institution_id", "dynamic_entities", "starter",
  "source", "is_locked", "last_ai_extraction", "source_documents",
  "locked_fields",
  "human_edited_fields",
  "migration_notes",
  // oncology-course anchor FK on the residue tables — relational, not clinical.
  "cancer", "cancer_id",
  // Imaging image-display wiring (P1): source-image link + resolved URL are
  // display-only, never clinical/editable fields.
  "image_document", "image_url",
]);

/** True only if rows contain at least one non-null clinical field value. */
function hasRealData(rows: TableRow[] | undefined): boolean {
  if (!rows || rows.length === 0) return false;
  return rows.some((row) =>
    Object.entries(row).some(
      ([key, value]) => !SYSTEM_KEYS.has(key) && value != null && value !== ""
    )
  );
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const NAVBAR_HEIGHT = 64;
const ACTION_PANEL_WIDTH = 60;
const DATA_PANEL_MIN_WIDTH = 280;
const CHAT_MIN_WIDTH = 320;
const CONTAINER_MARGIN = 16;
const CHAT_COLLAPSED_WIDTH = 48;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const PatientDetailsLayout: React.FC<PatientDetailsLayoutProps> = ({
  patientId,
  patientName,
  anonymizedPatientName: providedAnonymizedName,
  environmentNames,
  patientUpdatedAt,
  patientDomains,
  legacyTablesEnabled = true,
  canRevealPii,
  externalId,
  legacyId,
  domainTypes,
  selectedDomains,
  onDomainSelectionChange,
  suggestedDomains,
  tablesData,
  tablesLoading,
  isAIExtracting,
  onTablesDataChange,
  onDomainAdd,
  onRecordCreated,
  submittedDocuments,
  allDocuments,
  onDocumentDelete,
  onSubmit,
  isExtracting,
  isStaleProcessing,
  hasExtractionErrors,
  onRetryExtraction,
  onDeletePatient,
  onSharePatient,
  onGenerateReport,
  onExportData,
}) => {
  const theme = useTheme();

  const { t } = useTranslation();

  // Breakpoints
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // > 1200px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg")); // 600-1200px
  // Mobile is the default fallback (< 600px)

  // Layout mode: "classic" (3-column) or "sidebar" (sidebar + wide content + floating chat)
  // Default changed to "sidebar" (Sebastian's feedback version) — the classic layout
  // is kept available but no longer the default.
  const [layoutMode, setLayoutMode] = useState<"classic" | "sidebar">(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("patient-layout-mode") as "classic" | "sidebar") || "sidebar";
    }
    return "sidebar";
  });
  const toggleLayoutMode = useCallback(() => {
    setLayoutMode((prev) => {
      const next = prev === "classic" ? "sidebar" : "classic";
      localStorage.setItem("patient-layout-mode", next);
      return next;
    });
  }, []);

  // Sidebar layout state
  const [activeSidebarSection, setActiveSidebarSection] = useState("all");
  // Sidebar-driven record focus. `key` is the target section id (e.g. a domain's
  // `${domain}_event`, or "medications"/"adverse_events") so DataPanel routes the
  // focus to exactly one event table (no cross-talk between id-spaces). `section`
  // is a field-group name for record→group navigation; `nonce` re-triggers the
  // focus effect when the same record/group is re-selected.
  const [eventFocus, setEventFocus] = useState<{
    key: string;
    id: string | number;
    section?: string;
    nonce: number;
  } | null>(null);

  // Category modal state — opened from sidebar "+" button
  const [categoryModal, setCategoryModal] = useState<{
    sectionId: string;
    categoryName: string;
  } | null>(null);

  // Mobile drawer state
  const [mobileDataDrawerOpen, setMobileDataDrawerOpen] = useState(false);
  // mobileActionsDrawerOpen removed — actions are now in the patient header card

  // Collapse state (desktop/tablet) — only one panel can be collapsed at a time
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [dataPanelCollapsed, setDataPanelCollapsed] = useState(false);

  // Measure the actual container width with ResizeObserver so the column
  // widths match the real available space (the layout sits inside an 80%-wide
  // glass container, not the full viewport).
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) setContainerWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dataPanelDefaults = useMemo(() => {
    const w = containerWidth || (typeof window !== "undefined" ? window.innerWidth * 0.8 - 80 : 1000);
    const gaps = 32; // 2 gaps of 16px (gap: 2) between 3 columns
    const available = w - ACTION_PANEL_WIDTH - gaps;
    const defaultW = Math.floor(available * 0.55);
    const maxW = available - CHAT_MIN_WIDTH;
    return {
      defaultWidth: Math.max(Math.min(defaultW, maxW), DATA_PANEL_MIN_WIDTH),
      maxWidth: Math.max(maxW, DATA_PANEL_MIN_WIDTH),
    };
  }, [containerWidth]);

  // Anonymized name for display (prefer backend-provided, fallback to frontend anonymization)
  const anonymizedPatientName = providedAnonymizedName || (patientName ? anonymizeName(patientName) : undefined);

  // Handle mobile drawer toggles
  const toggleDataDrawer = useCallback(() => {
    setMobileDataDrawerOpen((prev) => !prev);
  }, []);

  // toggleActionsDrawer removed — actions are now in the patient header card

  // Container height calculation - fixed to viewport with margins
  const containerHeight = `calc(100vh - ${NAVBAR_HEIGHT}px - ${CONTAINER_MARGIN * 2}px)`;

  // Sidebar section data — show all selected domains, marking which have data.
  //
  // XXMM-synced patients carry the extraction-pipeline domain key
  // `medical.cancer.skin` in `patient.domains`, whereas the registry / domain-types
  // key (and the value returned by active-domains) is the un-prefixed `cancer.skin`.
  // Both end up in `selectedDomains`, so without normalization the sidebar renders
  // TWO headings for the same cancer: one raw (`MEDICAL.CANCER.SKIN`, no label match)
  // and one canonical (`Skin Cancer`). Collapse the `medical.`-prefixed alias onto its
  // canonical registry key — the same prefix normalization the backend value-set
  // resolver applies (`_canon()`), so a single, properly-labelled section renders.
  // Display-only: does not mutate or persist `selectedDomains`.
  const sidebarDomainSections = useMemo(() => {
    const domainValueSet = new Set(domainTypes.map((d) => d.value));
    const canonicalizeDomainKey = (key: string): string => {
      if (domainValueSet.has(key)) return key;
      if (key.startsWith("medical.")) {
        const stripped = key.slice("medical.".length);
        if (domainValueSet.has(stripped)) return stripped;
      }
      return key;
    };
    const domainKeys: string[] = [];
    const seen = new Set<string>();
    for (const rawKey of [
      ...Object.keys(tablesData.domains),
      ...selectedDomains,
    ]) {
      const canon = canonicalizeDomainKey(rawKey);
      if (!seen.has(canon)) {
        seen.add(canon);
        domainKeys.push(canon);
      }
    }
    return domainKeys.map((domainKey) => {
      const domainData = tablesData.domains[domainKey];
      const domainLabel = domainTypes.find((d) => d.value === domainKey)?.label || domainKey;
      return {
        domainKey,
        label: domainLabel,
        hasStatic: hasRealData(domainData?.static),
        hasEvent: hasRealData(domainData?.event),
        events: (domainData?.event ?? [])
          .map((row: any) => ({
            event_id: row.event_id ?? row.id,
            event_type: row.event_type as string | undefined,
            event_date: row.event_date as string | undefined,
          }))
          .sort((a, b) => {
            if (!a.event_date && !b.event_date) return 0;
            if (!a.event_date) return 1;
            if (!b.event_date) return -1;
            return new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
          }),
      };
    });
  }, [tablesData.domains, domainTypes, selectedDomains]);

  // Tables force-visible after sidebar (+) creation
  const [sidebarForceVisible, setSidebarForceVisible] = useState<Set<string>>(new Set());

  // Reset when patient changes
  useEffect(() => {
    setSidebarForceVisible(new Set());
  }, [patientId]);

  // Map tableType → existing rows for duplicate prevention
  const universalTableRows: Record<string, unknown[] | undefined> = useMemo(() => ({
    patientprofiletable: tablesData.universal?.patientProfile,
    molecularprofiletable: tablesData.universal?.molecularProfile,
    cancerprofiletable: tablesData.universal?.cancerProfile,
    cancerobservationtable: tablesData.universal?.cancerObservation,
    hematologicalprofiletable: tablesData.universal?.hematologicalProfile,
    trialenrollmenttable: tablesData.universal?.trialEnrollment,
    medicationstatementtable: tablesData.universal?.medications,
    diagnosistable: tablesData.universal?.diagnoses,
    procedurestable: tablesData.universal?.procedures,
    tumorassessmenttable: tablesData.universal?.tumorAssessments,
    diseasestatustable: tablesData.universal?.diseaseStatuses,
    imagingtable: tablesData.universal?.imaging,
    systemictherapytable: tablesData.universal?.systemicTherapies,
    labresulttable: tablesData.universal?.labResults,
    familyhistorytable: tablesData.universal?.familyHistory,
  }), [tablesData.universal]);

  // Create an empty universal table record from the sidebar (+) button
  const handleCreateUniversalTable = useCallback(
    async (tableType: string) => {
      if (!patientId) return;

      // Don't create a duplicate — if rows already exist, just force-show + navigate
      const existing = universalTableRows[tableType];
      if (existing && existing.length > 0) {
        setSidebarForceVisible((prev) => {
          const next = new Set(prev);
          next.add(tableType);
          return next;
        });
        return;
      }

      try {
        await createData(`/${tableType}/`, { patient: patientId });
        setSidebarForceVisible((prev) => {
          const next = new Set(prev);
          next.add(tableType);
          return next;
        });
        onRecordCreated?.();
      } catch (err) {
        logger.error("[Sidebar] Failed to create universal table:", err);
      }
    },
    [patientId, onRecordCreated, universalTableRows]
  );

  // Add a domain from the sidebar (+) button
  const handleSidebarAddDomain = useCallback(
    (domainKey: string) => {
      onDomainAdd?.(domainKey);
    },
    [onDomainAdd]
  );

  const handleSubItemClick = useCallback((sectionId: string, categoryName: string) => {
    setActiveSidebarSection(sectionId);

    // Record → field-group sub-item: pattern is "eventfield-{id}::{group}" —
    // open that record's detail modal deep-linked to the group.
    const groupMatch = categoryName.match(/^eventfield-(.+?)::(.+)$/);
    if (groupMatch) {
      setEventFocus((prev) => ({
        key: sectionId,
        id: groupMatch[1],
        section: groupMatch[2],
        nonce: (prev?.nonce ?? 0) + 1,
      }));
      requestAnimationFrame(() => {
        const sectionEl = document.getElementById(`data-section-${sectionId}`);
        sectionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }

    // Record sub-item: pattern is "event-{id}" — expand the row in EditableTable
    const eventMatch = categoryName.match(/^event-(.+)$/);
    if (eventMatch) {
      setEventFocus((prev) => ({
        key: sectionId,
        id: eventMatch[1],
        nonce: (prev?.nonce ?? 0) + 1,
      }));
      requestAnimationFrame(() => {
        const sectionEl = document.getElementById(`data-section-${sectionId}`);
        sectionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }

    // Category sub-item: scroll to the section, then to the category header
    setEventFocus(null);
    requestAnimationFrame(() => {
      const sectionEl = document.getElementById(`data-section-${sectionId}`);
      sectionEl?.scrollIntoView({ behavior: "smooth", block: "start" });

      const slugified = categoryName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
      setTimeout(() => {
        const catEl = document.getElementById(`field-category-${slugified}`)
          ?? document.getElementById(categoryName);
        if (catEl) {
          catEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    });
  }, []);

  // Sidebar "Add event" popover state
  const [addEventPopover, setAddEventPopover] = useState<{
    anchorEl: HTMLElement;
    domainKey: string;
  } | null>(null);

  const handleSidebarAddEvent = useCallback((domainKey: string, anchorEl?: HTMLElement) => {
    if (anchorEl) {
      setAddEventPopover({ anchorEl, domainKey });
    } else {
      setActiveSidebarSection(`${domainKey}_event`);
      requestAnimationFrame(() => {
        const el = document.getElementById(`data-section-${domainKey}_event`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          const btn = document.querySelector<HTMLElement>(`[data-add-event-domain="${domainKey}"] .MuiChip-root`);
          btn?.click();
        }, 350);
      });
    }
  }, []);

  const addEventDomainKey = addEventPopover?.domainKey ?? "";
  const addEventUrlDomain = addEventDomainKey.replace(/\./g, "-");
  const { schema: addEventSchema } = useFieldSchema(addEventDomainKey, "eventtable");
  const addEventChoices = addEventSchema?.fields?.event_type?.choices ?? [];

  const handleCreateEventFromSidebar = useCallback(
    async (eventType: string) => {
      if (!patientId || !addEventPopover) return;
      setAddEventPopover(null);
      const endpoint = `/${addEventUrlDomain}-eventtable/`;
      try {
        await createData(endpoint, { patient: patientId, event_type: eventType });
        onRecordCreated?.();
        setActiveSidebarSection(`${addEventPopover.domainKey}_event`);
      } catch (err) {
        logger.error("[Sidebar] Failed to create event:", err);
      }
    },
    [patientId, addEventPopover, addEventUrlDomain, onRecordCreated]
  );

  const handleCategoryAction = useCallback((sectionId: string, categoryName: string) => {
    setCategoryModal({ sectionId, categoryName });
  }, []);

  // Layout toggle button — kept for potential reuse but not rendered.
  // The sidebar layout is now the default; the classic layout is still accessible
  // programmatically via localStorage("patient-layout-mode") = "classic".
  const layoutToggleButton = (
    <Tooltip title={layoutMode === "classic" ? t("patient_layout.switch_sidebar", "Switch to sidebar layout") : t("patient_layout.switch_classic", "Switch to classic layout")}>
      <IconButton
        size="small"
        onClick={toggleLayoutMode}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 20,
          backgroundColor: alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.heavy || 0.88),
          border: `1px solid ${theme.palette.divider}`,
          width: 28,
          height: 28,
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        {layoutMode === "classic" ? (
          <ViewSidebar sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
        ) : (
          <ViewColumn sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
        )}
      </IconButton>
    </Tooltip>
  );
  void layoutToggleButton; // suppress unused-variable lint

  // =============================================================================
  // Shared sidebar + action button elements (used by all layouts below)
  // =============================================================================

  const sidebarElement = (
    <TableSidebar
      legacyTablesEnabled={legacyTablesEnabled}
      hasPatientProfile={hasRealData(tablesData.universal?.patientProfile)}
      hasMolecularProfile={hasRealData(tablesData.universal?.molecularProfile)}
      hasCancerProfile={hasRealData(tablesData.universal?.cancerProfile) || hasRealData(tablesData.universal?.cancerObservation)}
      hasHematologicalProfile={hasRealData(tablesData.universal?.hematologicalProfile)}
      hasTrialEnrollment={hasRealData(tablesData.universal?.trialEnrollment)}
      hasAdverseEvents={hasRealData(tablesData.universal?.adverseEvents)}
      hasMedications={hasRealData(tablesData.universal?.medications)}
      hasDiagnoses={hasRealData(tablesData.universal?.diagnoses)}
      hasProcedures={hasRealData(tablesData.universal?.procedures)}
      hasTumorAssessments={hasRealData(tablesData.universal?.tumorAssessments)}
      hasDiseaseStatuses={hasRealData(tablesData.universal?.diseaseStatuses)}
      hasImaging={hasRealData(tablesData.universal?.imaging)}
      hasSystemicTherapies={hasRealData(tablesData.universal?.systemicTherapies)}
      hasLabResults={hasRealData(tablesData.universal?.labResults)}
      hasFamilyHistory={hasRealData(tablesData.universal?.familyHistory)}
      universalData={tablesData.universal}
      allDomainData={tablesData.domains}
      domainSections={sidebarDomainSections}
      activeSection={activeSidebarSection}
      onSectionChange={(id) => {
        setActiveSidebarSection(id);
        if (!isDesktop) setMobileDataDrawerOpen(false);
      }}
      onCreateUniversalTable={handleCreateUniversalTable}
      availableDomainTypes={domainTypes}
      onAddDomain={handleSidebarAddDomain}
      onAddEvent={handleSidebarAddEvent}
      onSubItemClick={(sectionId, cat) => {
        handleSubItemClick(sectionId, cat);
        if (!isDesktop) setMobileDataDrawerOpen(false);
      }}
      onCategoryAction={handleCategoryAction}
      // "+ Add" on a universal event-style section (Medications / Adverse
      // Events): reuse the already-wired creation-modal path. Setting the
      // category modal for these sections is intercepted by DataPanel's
      // EVENT_STYLE_UNIVERSAL_KINDS routing effect, which opens the creation
      // modal (create a NEW record) instead of the singleton category modal.
      onCreateRecord={(sectionId) => handleCategoryAction(sectionId, "__create__")}
    />
  );

  const headerActionButtons = (
    <ActionPanel
      patientId={patientId}
      patientName={anonymizedPatientName || patientName}
      onDelete={onDeletePatient}
      onShare={onSharePatient}
      onExport={onExportData}
      layout="inline"
    />
  );

  const dataPanelElement = (
    <DataPanel
      patientId={patientId}
      anonymizedName={anonymizedPatientName}
      fullName={patientName}
      environmentNames={environmentNames}
      patientUpdatedAt={patientUpdatedAt}
      patientDomains={patientDomains}
      canRevealPii={canRevealPii}
      externalId={externalId}
      legacyId={legacyId}
      domainTypes={domainTypes}
      selectedDomains={selectedDomains}
      tablesData={tablesData}
      loading={tablesLoading}
      isAIExtracting={isAIExtracting}
      onTablesDataChange={onTablesDataChange}
      onDomainAdd={onDomainAdd}
      onRecordCreated={onRecordCreated}
      isStaleProcessing={isStaleProcessing}
      hasExtractionErrors={hasExtractionErrors}
      onRetryExtraction={onRetryExtraction}
      scrollToSection={activeSidebarSection}
      externalForceVisibleTables={sidebarForceVisible}
      sidebarMode
      eventFocus={eventFocus}
      headerActions={headerActionButtons}
      onGenerateReport={onGenerateReport}
      categoryModal={categoryModal}
      onCloseCategoryModal={() => setCategoryModal(null)}
      onActiveSectionChange={setActiveSidebarSection}
    />
  );

  // Event type popover — rendered once, shared across all layouts
  const addEventPopoverEl = (
    <Popover
      open={!!addEventPopover}
      anchorEl={addEventPopover?.anchorEl ?? null}
      onClose={() => setAddEventPopover(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          width: SIDEBAR_WIDTH,
          maxHeight: 360,
          borderRadius: 2,
          background: `linear-gradient(160deg, ${alpha(theme.palette.primary.dark, 0.97)} 0%, ${alpha(theme.palette.primary.dark, 0.93)} 100%)`,
          backdropFilter: "blur(12px)",
          border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
          boxShadow: theme.custom?.glassShadow || `0 8px 32px ${alpha(theme.palette.primary.dark, 0.3)}`,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}` }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: alpha(theme.palette.common.white, 0.55),
            fontSize: "0.65rem",
          }}
        >
          {t("add_event.select_type", "Select event type")}
        </Typography>
      </Box>
      <List dense sx={{ maxHeight: 300, overflow: "auto", py: 0.5 }}>
        {addEventChoices
          .filter((c) => c.value !== null && c.value !== "")
          .map((choice) => (
            <ListItemButton
              key={String(choice.value)}
              onClick={() => handleCreateEventFromSidebar(String(choice.value))}
              sx={{
                py: 0.6,
                px: 2,
                borderRadius: 1,
                mx: 0.5,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.08),
                },
              }}
            >
              <ListItemText
                primary={choice.display_name}
                primaryTypographyProps={{
                  variant: "body2",
                  sx: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: alpha(theme.palette.common.white, 0.85),
                  },
                }}
              />
            </ListItemButton>
          ))}
      </List>
    </Popover>
  );

  const containerRadius = isDesktop ? 24 : 16; // matches LayoutContainer borderRadius 3 / 2

  const renderChat = (positioning?: { left?: string | number; right?: string | number; bottomRightRadius?: number }) => (
    <FloatingChatPanel
      left={positioning?.left}
      right={positioning?.right ?? 0}
      bottomRightRadius={positioning?.bottomRightRadius}
    >
      <ChatPanel
        domainTypes={domainTypes}
        selectedDomains={selectedDomains}
        onDomainSelectionChange={onDomainSelectionChange}
        suggestedDomains={suggestedDomains}
        submittedDocuments={submittedDocuments}
        allDocuments={allDocuments}
        onDocumentDelete={undefined}
        onSubmit={onSubmit}
        isExtracting={isExtracting}
      />
    </FloatingChatPanel>
  );

  // =============================================================================
  // DESKTOP: Sidebar always visible on the left
  // =============================================================================
  if (isDesktop && layoutMode === "sidebar") {
    return (
      <Box
        data-tour="patient-details-workspace"
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {sidebarElement}

        <Box
          id="data-panel-scroll-container"
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            overflow: "auto",
            // top = 8 (with DataPanel pt:0) and right = 8 (with DataPanel's
            // px:1=8). LEFT padding kept at 16 so the gap to the sidebar (first
            // column) is exactly as before. Bottom keeps the floating-chat
            // clearance.
            pt: 1,
            pl: 2,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.dark, 0.15),
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }}
        >
          {dataPanelElement}
        </Box>

        {renderChat({
          // Align the chat's left/right edges with the column's cards. The cards
          // sit at sidebar + scroll-container pl (16) + DataPanel px (8) = +24,
          // and 8px from the right; match both so the chat lines up exactly.
          left: `calc(${SIDEBAR_WIDTH}px + ${theme.spacing(3)})`,
          right: theme.spacing(1),
          bottomRightRadius: containerRadius,
        })}
        {addEventPopoverEl}
      </Box>
    );
  }

  // =============================================================================
  // DESKTOP CLASSIC LAYOUT (3 columns) — kept but no longer default
  // =============================================================================
  if (isDesktop) {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          width: "100%",
          gap: 2,
          p: 2,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <ResizablePanel
          defaultWidth={dataPanelDefaults.defaultWidth}
          minWidth={DATA_PANEL_MIN_WIDTH}
          maxWidth={chatCollapsed ? 9999 : dataPanelDefaults.maxWidth}
          resizeEdge="right"
          collapsible
          collapseDisabled={chatCollapsed}
          collapseLabel="Data"
          onCollapsedChange={setDataPanelCollapsed}
          storageKey="patient-data-panel"
          sx={chatCollapsed ? { flex: 1 } : undefined}
        >
          <DataPanel
            patientId={patientId}
            anonymizedName={anonymizedPatientName}
            fullName={patientName}
            environmentNames={environmentNames}
            patientUpdatedAt={patientUpdatedAt}
            patientDomains={patientDomains}
            canRevealPii={canRevealPii}
            externalId={externalId}
            legacyId={legacyId}
            domainTypes={domainTypes}
            selectedDomains={selectedDomains}
            tablesData={tablesData}
            loading={tablesLoading}
            isAIExtracting={isAIExtracting}
            onTablesDataChange={onTablesDataChange}
            onDomainAdd={onDomainAdd}
            onRecordCreated={onRecordCreated}
            isStaleProcessing={isStaleProcessing}
            hasExtractionErrors={hasExtractionErrors}
            onRetryExtraction={onRetryExtraction}
            externalForceVisibleTables={sidebarForceVisible}
          />
        </ResizablePanel>

        <Box
          sx={{
            flex: chatCollapsed ? "none" : 1,
            width: chatCollapsed ? CHAT_COLLAPSED_WIDTH : "auto",
            minWidth: chatCollapsed ? CHAT_COLLAPSED_WIDTH : CHAT_MIN_WIDTH,
            height: "100%",
            position: "relative",
            transition: "flex 0.2s ease, width 0.2s ease, min-width 0.2s ease",
            flexShrink: chatCollapsed ? 0 : 1,
            overflow: "hidden",
          }}
        >
          {chatCollapsed ? (
            <Tooltip title={t("patient_details.expand_chat", "Expand chat")}>
              <Box
                onClick={() => setChatCollapsed(false)}
                role="button"
                tabIndex={0}
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 4,
                  transform: "rotate(90deg)",
                  transformOrigin: "top left",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  zIndex: 10,
                  backgroundColor: alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.heavy || 0.88),
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <ChevronLeft sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {t("patient_layout.chat", "Chat")}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title={dataPanelCollapsed ? t("patient_layout.expand_other_first", "Expand other panel first") : t("patient_layout.collapse_chat", "Collapse chat")} placement="right">
              <span>
                <Box
                  onClick={dataPanelCollapsed ? undefined : () => setChatCollapsed(true)}
                  role="button"
                  tabIndex={0}
                  sx={{
                    position: "absolute", top: 8, left: 8, zIndex: 10,
                    backgroundColor: alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.heavy || 0.88),
                    border: `1px solid ${theme.palette.divider}`,
                    width: 24, height: 24, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: dataPanelCollapsed ? "default" : "pointer",
                    opacity: dataPanelCollapsed ? 0.4 : 1,
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: dataPanelCollapsed ? undefined : theme.palette.action.hover },
                  }}
                >
                  <ChevronRight sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                </Box>
              </span>
            </Tooltip>
          )}

          <Box sx={{ height: "100%", opacity: chatCollapsed ? 0 : 1, visibility: chatCollapsed ? "hidden" : "visible", transition: "opacity 0.2s ease", overflow: "hidden" }}>
            <ChatPanel
              domainTypes={domainTypes}
              selectedDomains={selectedDomains}
              onDomainSelectionChange={onDomainSelectionChange}
              suggestedDomains={suggestedDomains}
              submittedDocuments={submittedDocuments}
              allDocuments={allDocuments}
              onDocumentDelete={undefined}
              onSubmit={onSubmit}
              isExtracting={isExtracting}
            />
          </Box>
        </Box>

        <Box sx={{ width: ACTION_PANEL_WIDTH, flexShrink: 0, height: "100%", backgroundColor: alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.light || 0.6), borderRadius: 2, backdropFilter: "blur(8px)", border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
          <ActionPanel patientId={patientId} patientName={anonymizedPatientName || patientName} onDelete={onDeletePatient} onShare={onSharePatient} onExport={onExportData} />
        </Box>
        {addEventPopoverEl}
      </Box>
    );
  }

  // =============================================================================
  // TABLET + MOBILE: Responsive sidebar layout
  // Sidebar opens as a drawer, tables shown as main content, floating chat bottom
  // =============================================================================
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Sidebar toggle — top-left FAB */}
      <Fab
        size="small"
        onClick={toggleDataDrawer}
        sx={{
          position: "fixed",
          top: NAVBAR_HEIGHT + 12,
          left: 12,
          zIndex: theme.zIndex.speedDial,
          width: 36,
          height: 36,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
          color: theme.palette.common.white,
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          "&:hover": {
            background: theme.palette.primary.dark,
          },
        }}
      >
        <ViewSidebar sx={{ fontSize: 18 }} />
      </Fab>

      {/* Main scrollable content — tables */}
      <Box
        id="data-panel-scroll-container"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 1, sm: 2 },
          pb: 8,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.primary.dark, 0.15),
            borderRadius: 2,
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        {dataPanelElement}
      </Box>

      {/* Floating Chat — bottom, symmetric spacing */}
      {renderChat({
        left: theme.spacing(4),
        right: theme.spacing(4),
        bottomRightRadius: containerRadius,
      })}

      {/* Sidebar Drawer — same TableSidebar component as desktop */}
      <Drawer
        anchor="left"
        open={mobileDataDrawerOpen}
        onClose={toggleDataDrawer}
        PaperProps={{
          sx: {
            width: isTablet ? 260 : "80%",
            maxWidth: 280,
            p: 0,
            overflow: "hidden",
            background: "transparent",
            boxShadow: "none",
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ height: "100%", overflow: "hidden" }}>
          {sidebarElement}
        </Box>
      </Drawer>
      {addEventPopoverEl}
    </Box>
  );
};

export default PatientDetailsLayout;
